import type { Ohaeng } from '../types/index';
import type { FourPillars } from '../types/index';
import type { YongSinResult } from '../types/index';
import type { EarthlyBranch } from '../types/index';
import { getStemElement, getElementRelation } from '../core/five-elements';
import { findHanSin } from './yong-sin';

/**
 * 조후법 (調候法) — 계절 기후에 따른 한난조습 조절 용신법
 *
 * 원리: 월지(계절)에 따라 사주의 한난조습을 판단하고,
 * 부족한 기운을 보충하는 오행을 용신으로 선정
 */

interface ClimateInfo {
  climate: '극한' | '한' | '온' | '열' | '극열' | '조' | '습';
  applicability: number;
}

const BRANCH_CLIMATE: Record<EarthlyBranch, ClimateInfo> = {
  '子': { climate: '극한', applicability: 1.0 },
  '丑': { climate: '한', applicability: 0.7 },
  '寅': { climate: '온', applicability: 0.5 },
  '卯': { climate: '온', applicability: 0.4 },
  '辰': { climate: '습', applicability: 0.5 },
  '巳': { climate: '열', applicability: 0.8 },
  '午': { climate: '극열', applicability: 1.0 },
  '未': { climate: '열', applicability: 0.7 },
  '申': { climate: '온', applicability: 0.5 },
  '酉': { climate: '온', applicability: 0.4 },
  '戌': { climate: '조', applicability: 0.5 },
  '亥': { climate: '한', applicability: 0.8 },
};

/**
 * 조후 용신 테이블: 일간오행(5) × 월지(12) = 60 조합
 * [용신, 희신] 형태
 *
 * v1: 일간 오행 기준 (음양 구분 없음)
 */
const JOHU_TABLE: Record<Ohaeng, Record<EarthlyBranch, [Ohaeng, Ohaeng]>> = {
  '木': {
    '子': ['火', '水'], '丑': ['火', '木'], '寅': ['水', '火'], '卯': ['水', '金'],
    '辰': ['水', '火'], '巳': ['水', '金'], '午': ['水', '金'], '未': ['水', '金'],
    '申': ['火', '水'], '酉': ['火', '水'], '戌': ['水', '火'], '亥': ['火', '木'],
  },
  '火': {
    '子': ['木', '火'], '丑': ['木', '火'], '寅': ['木', '水'], '卯': ['土', '水'],
    '辰': ['木', '水'], '巳': ['水', '金'], '午': ['水', '金'], '未': ['水', '金'],
    '申': ['木', '火'], '酉': ['木', '火'], '戌': ['木', '水'], '亥': ['木', '火'],
  },
  '土': {
    '子': ['火', '木'], '丑': ['火', '木'], '寅': ['火', '木'], '卯': ['火', '金'],
    '辰': ['火', '金'], '巳': ['水', '金'], '午': ['水', '金'], '未': ['水', '金'],
    '申': ['火', '木'], '酉': ['火', '木'], '戌': ['水', '金'], '亥': ['火', '木'],
  },
  '金': {
    '子': ['火', '土'], '丑': ['火', '土'], '寅': ['火', '土'], '卯': ['火', '土'],
    '辰': ['火', '水'], '巳': ['水', '木'], '午': ['水', '木'], '未': ['水', '木'],
    '申': ['水', '火'], '酉': ['水', '火'], '戌': ['水', '火'], '亥': ['火', '土'],
  },
  '水': {
    '子': ['火', '土'], '丑': ['火', '金'], '寅': ['火', '金'], '卯': ['火', '金'],
    '辰': ['火', '土'], '巳': ['金', '水'], '午': ['金', '水'], '未': ['金', '水'],
    '申': ['火', '土'], '酉': ['火', '土'], '戌': ['火', '金'], '亥': ['火', '土'],
  },
};

/**
 * 조후법에 의한 용신 판단
 */
export function determineJohuYongSin(
  fourPillars: FourPillars,
): YongSinResult {
  const dayMasterElement = getStemElement(fourPillars.day.stem);
  const monthBranch = fourPillars.month.branch;
  const climateInfo = BRANCH_CLIMATE[monthBranch];

  const [yongSinElement, huiSinElement] = JOHU_TABLE[dayMasterElement][monthBranch];

  const relation = getElementRelation(yongSinElement);
  const giSin = relation.overcomeBy;
  const huiRelation = getElementRelation(huiSinElement);
  const guSin = huiRelation.overcomeBy;
  const hanSin = findHanSin(yongSinElement, huiSinElement, giSin, guSin);

  const climateDescription: Record<ClimateInfo['climate'], string> = {
    '극한': '극한(엄동설한)의 기후로 온난한 기운이 절실',
    '한': '한랭한 기후로 온난한 기운이 필요',
    '온': '온화한 기후로 조후 영향이 적음',
    '열': '뜨거운 기후로 냉각 기운이 필요',
    '극열': '극열(한여름)의 기후로 냉각 기운이 절실',
    '조': '건조한 환절기로 습윤한 기운이 필요',
    '습': '습한 환절기로 조절이 필요',
  };

  return {
    method: '조후',
    yongSin: yongSinElement,
    huiSin: huiSinElement,
    giSin,
    guSin,
    hanSin,
    applicability: climateInfo.applicability,
    description: `월지 ${monthBranch}(${climateDescription[climateInfo.climate]}), ${dayMasterElement}일간에 ${yongSinElement}이(가) 조후 용신`,
  };
}
